import {
  useEffect,
  useRef,
  type ChangeEvent,
  type ClipboardEvent,
  type KeyboardEvent,
} from "react";
import { WarningCircleIcon } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";

export const VERIFICATION_CODE_LENGTH = 6;

export interface VerificationCodeInputProps {
  /** Prefix for the cell ids and the error id (`${id}-error`). */
  id: string;
  /** The digits typed so far, at most 6 characters. */
  value: string;
  onChange: (value: string) => void;
  /** Fires once all six digits are present. */
  onComplete?: (value: string) => void;
  /** Inline error; renders the warning row and marks every cell invalid. */
  error?: string;
  disabled?: boolean;
  /** id of the visible text that names the group (WCAG 1.3.1 / 4.1.2). */
  labelledBy: string;
  /** Accessible name per cell; `{n}` is replaced with the 1-based position. */
  digitLabel: string;
  /** Moves focus to the first cell on mount. */
  autoFocus?: boolean;
  className?: string;
}

const onlyDigits = (raw: string) => raw.replace(/\D/g, "");

/**
 * Six single-digit cells for the email verification code (Figma 16232:296324).
 * Typing advances, Backspace on an empty cell retreats, a paste fills from the
 * first cell; the group is one form control to a screen reader.
 */
const VerificationCodeInput = ({
  id,
  value,
  onChange,
  onComplete,
  error,
  disabled = false,
  labelledBy,
  digitLabel,
  autoFocus = false,
  className,
}: VerificationCodeInputProps) => {
  const cellRefs = useRef<(HTMLInputElement | null)[]>([]);
  const errorId = `${id}-error`;
  const digits = Array.from(
    { length: VERIFICATION_CODE_LENGTH },
    (_, i) => value[i] ?? "",
  );

  useEffect(() => {
    if (autoFocus) cellRefs.current[0]?.focus();
  }, [autoFocus]);

  const focusCell = (index: number) => {
    const clamped = Math.max(0, Math.min(VERIFICATION_CODE_LENGTH - 1, index));
    cellRefs.current[clamped]?.focus();
    cellRefs.current[clamped]?.select();
  };

  const commit = (next: string) => {
    const trimmed = next.slice(0, VERIFICATION_CODE_LENGTH);
    onChange(trimmed);
    if (trimmed.length === VERIFICATION_CODE_LENGTH) onComplete?.(trimmed);
  };

  const handleChange = (index: number, e: ChangeEvent<HTMLInputElement>) => {
    const typed = onlyDigits(e.target.value);
    if (!typed) {
      // The value is contiguous, so a cleared cell truncates from there rather than
      // shifting later digits into the wrong slots.
      commit(value.slice(0, index));
      return;
    }
    // A cell can receive several digits (autofill, IME); they overwrite from this cell
    // on and the untouched tail is kept.
    const next = (
      value.slice(0, index) +
      typed +
      value.slice(index + typed.length)
    ).slice(0, VERIFICATION_CODE_LENGTH);
    commit(next);
    focusCell(Math.min(index + typed.length, next.length));
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      e.preventDefault();
      commit(value.slice(0, index - 1));
      focusCell(index - 1);
    } else if (e.key === "ArrowLeft" && index > 0) {
      e.preventDefault();
      focusCell(index - 1);
    } else if (e.key === "ArrowRight" && index < VERIFICATION_CODE_LENGTH - 1) {
      e.preventDefault();
      focusCell(index + 1);
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const pasted = onlyDigits(e.clipboardData.getData("text"));
    if (!pasted) return;
    e.preventDefault();
    const next = pasted.slice(0, VERIFICATION_CODE_LENGTH);
    commit(next);
    focusCell(next.length - 1);
  };

  return (
    <div className={cn("flex flex-col gap-4", className)}>
      {/* 8 px gap at the 360 px base so six 44 px cells fit the 312 px form width; Figma's 12 px from tablet. */}
      <div
        role="group"
        aria-labelledby={labelledBy}
        aria-describedby={error ? errorId : undefined}
        className="flex w-full items-start justify-center gap-2 tablet:gap-3"
      >
        {digits.map((digit, index) => (
          <input
            key={index}
            id={`${id}-${index + 1}`}
            ref={(el) => {
              cellRefs.current[index] = el;
            }}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={1}
            autoComplete={index === 0 ? "one-time-code" : "off"}
            value={digit}
            disabled={disabled}
            aria-label={digitLabel.replace("{n}", String(index + 1))}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? errorId : undefined}
            onChange={(e) => handleChange(index, e)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            onFocus={(e) => e.currentTarget.select()}
            className={cn(
              "min-h-[48px] min-w-[44px] flex-1 rounded-lg border px-4 py-3 text-center label-m-medium text-fg-neutral-strong bg-transparent focus-blue",
              error
                ? "border-border-token-error-subtle bg-bg-error-tertiary-default"
                : "border-border-token-neutral-strong",
              disabled && "opacity-60",
            )}
          />
        ))}
      </div>
      {error && (
        <p
          id={errorId}
          role="alert"
          className="flex items-center gap-1.5 label-s-medium text-fg-error-subtle"
        >
          <WarningCircleIcon size={16} aria-hidden="true" focusable="false" />
          <span>{error}</span>
        </p>
      )}
    </div>
  );
};

export default VerificationCodeInput;
